import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Download, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { ColumnDef, ColumnFiltersState, SortingState } from "@/components/ui/data-table";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { PageNote } from "@/components/ui/page-legend";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CennikBledyTable } from "@/features/contracts/cennik-bledy-table";
import { etykietaKoduCennika, pobranieBezPozycji } from "@/features/contracts/cennik-naglowek";
import {
	komunikatPrzekroczeniaCzasu,
	rodzajWynikuZlecenia,
	zlecenieNieodebrane,
} from "@/features/contracts/cennik-pobranie";
import { CennikPorownaniePanel } from "@/features/contracts/cennik-porownanie-panel";
import type { CennikPozycjaRow } from "@/features/contracts/contracts-api";
import { contractsApi } from "@/features/contracts/contracts-api";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { getCurrentUser } from "@/lib/auth-api";
import { formatPLN } from "@/lib/format";
import { ensureAuthenticated } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

// Backend dopuszcza tylko te role (poza nimi 403). Guard klienta odwzorowuje to,
// zeby analityk/superadmin wchodzili, a reszta dostawala redirect do dashboardu.
// Wzorzec identyczny jak w cenniki-historia.tsx.
const ALLOWED_ROLES = new Set(["super_admin", "analyst"]);

/**
 * Stan strony w URL — przeładowanie ma przywrócić widok, a link ma dać się wysłać.
 *
 * Zasada dla porównania: URL opisuje porównanie ZASTOSOWANE (zapisane kliknięciem
 * „Porównaj"), a nie to, co ktoś właśnie klika w formularzu. Gdyby lądował tam
 * każdy ruch, powrót ze świeżej karty odtwarzałby półgotowe filtry jako wynik,
 * którego nikt nie policzył. Sortowanie i filtry nagłówków zapisujemy od razu —
 * one doprecyzowują już policzony wynik.
 */
const searchSchema = z.object({
	idfdost: z.number().optional(),
	segment: z.enum(["baza", "franczyza"]).optional(),
	zakladka: z.enum(["cennik", "porownanie"]).optional(),
	scope: z.string().optional(),
	// Podgląd cennika
	cSort: z.string().optional(),
	cSortDir: z.enum(["asc", "desc"]).optional(),
	cFiltry: z.string().optional(),
	// Porównanie — zastosowane źródło B i parametry, którymi policzono wynik
	pB: z.number().optional(),
	pSegmentB: z.enum(["baza", "franczyza"]).optional(),
	pMiesiecy: z.number().optional(),
	pKupowane: z.boolean().optional(),
	pSort: z.string().optional(),
	pSortDir: z.enum(["asc", "desc"]).optional(),
	pFiltry: z.string().optional(),
});

export type CennikiPodgladSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_auth/kartoteka/cenniki-podglad")({
	component: CennikiPodgladPage,
	validateSearch: (s: Record<string, unknown>): CennikiPodgladSearch => searchSchema.parse(s),
	beforeLoad: async () => {
		await ensureAuthenticated();
		const user = getCurrentUser();
		if (!user || !user.roles.some((r) => ALLOWED_ROLES.has(r))) {
			throw redirect({ to: "/dashboard" });
		}
	},
});

const PAGE_SIZE = 100;

type Segment = "baza" | "franczyza";

/** Bezpieczna konwersja string->number (ceny przychodza jako string). null gdy brak/NaN. */
function toNum(v?: string): number | null {
	if (v == null || v === "") return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}

/** Cena z gwardia — "—" gdy brak/NaN. */
function cena(v?: string): string {
	const n = toNum(v);
	return n != null ? formatPLN(n) : "—";
}

/** VAT jako np. "23%" — "—" gdy brak. */
function vatPct(v?: string): string {
	const n = toNum(v);
	return n != null ? `${n}%` : "—";
}

/** Lokalna data (bez toISOString — to konwertuje do UTC i cofa dzien tuz po polnocy). */
function fmtDate(iso?: string): string {
	if (!iso) return "—";
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Lokalna data + godzina (do wyswietlenia momentu pobrania cennika). */
function fmtDateTime(iso?: string): string {
	if (!iso) return "—";
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleString("pl-PL", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
}

// Poll status co 2.5s; twardy limit ~3 min zanim odpuścimy (daemon zwykle łapie w ~20s).
const POLL_INTERVAL_MS = 2500;
const POLL_CAP_MS = 3 * 60 * 1000;

function CennikiPodgladPage() {
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	/** Zapis pojedynczych pól do URL — `replace`, żeby zmiana filtra nie zaśmiecała
	 *  historii przeglądarki jednym wpisem na każde kliknięcie. */
	const patchSearch = useCallback(
		(patch: Partial<CennikiPodgladSearch>) => {
			void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
		},
		[navigate],
	);

	const idfdost = search.idfdost ?? null;
	const setIdfdost = (v: number | null) => patchSearch({ idfdost: v ?? undefined });
	const segment: Segment = search.segment ?? "baza";
	const setSegment = (v: Segment) => patchSearch({ segment: v });
	const [query, setQuery] = useState("");
	const [offset, setOffset] = useState(0);
	const [bledyOpen, setBledyOpen] = useState(true);
	// Sortowanie i filtry naglowkow ida na SERWER — lista jest stronicowana, wiec
	// wersje klienckie porzadkowalyby i odsiewalyby tylko wczytana strone.
	const sorting: SortingState = [{ id: search.cSort ?? "nazwa", desc: search.cSortDir === "desc" }];
	const setSorting = (next: SortingState | ((s: SortingState) => SortingState)) => {
		const v = typeof next === "function" ? next(sorting) : next;
		patchSearch({ cSort: v[0]?.id, cSortDir: v[0]?.desc ? "desc" : "asc" });
	};
	const columnFilters: ColumnFiltersState = useMemo(
		() => (search.cFiltry ? JSON.parse(search.cFiltry) : []),
		[search.cFiltry],
	);
	const setColumnFilters = (
		next: ColumnFiltersState | ((s: ColumnFiltersState) => ColumnFiltersState),
	) => {
		const v = typeof next === "function" ? next(columnFilters) : next;
		patchSearch({ cFiltry: v.length > 0 ? JSON.stringify(v) : undefined });
	};
	const eksportRef = useRef<(() => Promise<void>) | null>(null);
	const debouncedQ = useDebounce(query, 300);
	const queryClient = useQueryClient();

	// Id zlecenia ręcznego pobrania cennika w toku (null => brak). Steruje pollingiem + spinnerem.
	const [fetchingJobId, setFetchingJobId] = useState<number | null>(null);
	// Timestamp startu pollingu — do twardego limitu ~3 min (safety cap).
	const pollStartedAtRef = useRef<number | null>(null);

	// Lista hurtowni ze skonfigurowanym źródłem cennika (super_admin + analyst).
	const { data: hurtownie = [] } = useQuery({
		queryKey: ["cenniki", "hurtownie"],
		queryFn: contractsApi.getCennikHurtownie,
	});

	const wholesalers = useMemo(
		() =>
			[...hurtownie].sort((a, b) =>
				(a.nazwa || `#${a.idfdost}`).localeCompare(b.nazwa || `#${b.idfdost}`, "pl"),
			),
		[hurtownie],
	);

	const selectedHurt = wholesalers.find((w) => w.idfdost === idfdost);
	// Franczyza wybrana dla hurtu bez AF → obowiązuje cennik AW (backend zwraca dane AW).
	const fallbackAf = segment === "franczyza" && selectedHurt != null && !selectedHurt.maFranczyza;

	// Reset paginacji przy kazdej zmianie filtra — deps to celowe triggery (setOffset stabilny).
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset offsetu na zmiane filtra
	useEffect(() => {
		setOffset(0);
	}, [idfdost, segment, debouncedQ, sorting, columnFilters]);

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ["cenniki", "pozycje", idfdost, segment, debouncedQ, offset, sorting, columnFilters],
		queryFn: () =>
			contractsApi.getCennikPozycje({
				idfdost: idfdost as number,
				segment,
				q: debouncedQ,
				limit: PAGE_SIZE,
				offset,
			}),
		enabled: idfdost != null,
		placeholderData: keepPreviousData,
	});

	// Błędy/anomalie pobranego cennika FTP (dziś multi_bloz) — baner nad tabelą.
	const { data: bledy = [] } = useQuery({
		queryKey: ["cenniki", "bledy", idfdost, segment],
		queryFn: () => contractsApi.getCennikBledy(idfdost as number, segment),
		enabled: idfdost != null,
	});

	// POST: zlec ręczne pobranie cennika wybranej hurtowni -> uruchom polling statusu.
	const zlecPobranie = useMutation({
		mutationFn: (id: number) => contractsApi.zlecPobranieCennika(id),
		onSuccess: ({ jobId }) => {
			pollStartedAtRef.current = Date.now();
			setFetchingJobId(jobId);
		},
		onError: (e) => {
			toast.error(e instanceof Error ? e.message : "Nie udało się zlecić pobrania");
		},
	});

	// POLL: co 2.5s odpytuj status; refetchInterval zwraca false gdy done/error (stop).
	const { data: pobierzStatus } = useQuery({
		queryKey: ["cenniki", "pobierz-status", fetchingJobId],
		queryFn: () => contractsApi.statusPobraniaCennika(fetchingJobId as number),
		enabled: fetchingJobId != null,
		refetchInterval: (q) => {
			const s = q.state.data?.status;
			if (s === "done" || s === "error") return false;
			return POLL_INTERVAL_MS;
		},
	});

	// Reakcja na wynik pollingu: sukces/błąd/limit czasu -> toast, sprzątanie, invalidacja.
	useEffect(() => {
		if (fetchingJobId == null) return;

		// Safety cap: po ~3 min odpuszczamy (daemon może być zajęty/offline).
		const startedAt = pollStartedAtRef.current;
		if (startedAt != null && Date.now() - startedAt > POLL_CAP_MS) {
			toast(komunikatPrzekroczeniaCzasu(pobierzStatus?.status));
			setFetchingJobId(null);
			pollStartedAtRef.current = null;
			return;
		}

		if (pobierzStatus?.status === "done" || pobierzStatus?.status === "error") {
			// `done` z `blad>0` (częściowy sukces albo zlecenie sprzed reguły `error`)
			// nie może wyglądać jak sukces — w wyniku jest przyczyna od daemona.
			const rodzaj = rodzajWynikuZlecenia(pobierzStatus.status, pobierzStatus.wynik);
			if (rodzaj === "sukces") {
				toast.success(pobierzStatus.wynik ?? "Cennik zaktualizowany");
			} else if (rodzaj === "ostrzezenie") {
				toast.warning(pobierzStatus.wynik ?? "Część źródeł nie została pobrana");
			} else {
				toast.error(pobierzStatus.wynik ?? "Pobranie nieudane");
			}
			setFetchingJobId(null);
			pollStartedAtRef.current = null;
			// Partial key: odświeża tabelę ORAZ kartę nagłówka (naglowek jest częścią tej odpowiedzi).
			if (pobierzStatus.status === "done") {
				void queryClient.invalidateQueries({ queryKey: ["cenniki", "pozycje"] });
			}
		}
	}, [pobierzStatus, fetchingJobId, queryClient]);

	const isFetchingCennik = fetchingJobId != null;

	// Polling odświeża komponent co 2,5 s, więc `Date.now()` przelicza się tu
	// naturalnie — nie potrzeba osobnego timera tylko po to, by odmierzyć próg.
	const nieodebrane =
		isFetchingCennik &&
		pollStartedAtRef.current != null &&
		zlecenieNieodebrane(pobierzStatus?.status, Date.now() - pollStartedAtRef.current);

	const naglowek = data?.naglowek ?? null;
	// Puste pobranie zostawia w bazie pozycje z poprzedniego pliku — wtedy nagłówek
	// opisuje starszy cennik i trzeba to powiedzieć wprost, nie przemilczeć.
	const pusteOstatnie = pobranieBezPozycji(naglowek, data?.ostatniePobranie ?? null);
	const pozycje = data?.pozycje ?? [];
	const total = data?.total ?? 0;

	const etykietaKodu = etykietaKoduCennika(pozycje);

	// Kolumny podgladu — te same zasady co w porownaniu: naglowek kodu zalezy od
	// przestrzeni (BLOZ/EAN), filtry liczbowe daja zakresy zamiast pojedynczego
	// dopasowania tekstem.
	const kolumnyCennika = useMemo<ColumnDef<CennikPozycjaRow>[]>(
		() => [
			// Telefon: tytulem wiersza jest nazwa, kod (BLOZ/EAN) idzie pod spod.
			{
				accessorKey: "kod",
				header: etykietaKodu,
				meta: { filterVariant: "text", mobileRole: "subtitle" },
			},
			{
				accessorKey: "nazwa",
				header: "Nazwa",
				meta: { filterVariant: "text", mobileRole: "title" },
			},
			{
				accessorKey: "cenaNetto",
				header: "Cena netto",
				cell: ({ row }) => cena(row.original.cenaNetto),
				meta: { isNumeric: true, filterVariant: "number" },
			},
			{
				accessorKey: "vat",
				header: "VAT",
				cell: ({ row }) => vatPct(row.original.vat),
				// Stawka VAT nie jest miara do porownywania — na telefonie w szczegolach.
				meta: { isNumeric: true, filterVariant: "number", mobileRole: "detail" },
			},
		],
		[etykietaKodu],
	);

	const fromRow = total === 0 ? 0 : offset + 1;
	const toRow = Math.min(offset + PAGE_SIZE, total);
	const canPrev = offset > 0;
	const canNext = offset + PAGE_SIZE < total;

	// Telefon: hurtownia i segment w chipach/arkuszu filtrow, wyszukiwarka nad tabela.
	const isBelowMd = useIsBelowMd();
	const hurtowniaSelect = (
		<Select
			value={idfdost != null ? String(idfdost) : undefined}
			onValueChange={(v) => setIdfdost(Number(v))}
		>
			<SelectTrigger className="h-10 w-full">
				<SelectValue placeholder="Wybierz hurtownię" />
			</SelectTrigger>
			<SelectContent>
				{wholesalers.map((w) => (
					<SelectItem key={w.idfdost} value={String(w.idfdost)}>
						{w.nazwa || `#${w.idfdost}`}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
	const segmentSelect = (
		<Select value={segment} onValueChange={(v) => setSegment(v as Segment)}>
			<SelectTrigger className="h-10 w-full">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="baza">Baza (AW)</SelectItem>
				<SelectItem value="franczyza">
					{selectedHurt && !selectedHurt.maFranczyza ? "Franczyza (= AW)" : "Franczyza (AF)"}
				</SelectItem>
			</SelectContent>
		</Select>
	);

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Podgląd cennika (hurt)"
					description="Pełny aktualny cennik wybranej hurtowni i segmentu — z datą pobrania, paginacją i wyszukiwaniem"
				/>
			</div>

			{isBelowMd && (
				<div className="px-3 pt-2 pb-2 border-t bg-card/50">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
						<Input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={`Szukaj po nazwie lub ${etykietaKodu}…`}
							className="h-10 pl-10 bg-background"
							disabled={idfdost == null}
						/>
					</div>
				</div>
			)}
			<MobileFilters
				chips={[
					{
						id: "hurtownia",
						label: "Hurtownia",
						value: selectedHurt
							? selectedHurt.nazwa || `#${selectedHurt.idfdost}`
							: "Wybierz hurtownię",
						active: idfdost != null,
						picker: hurtowniaSelect,
					},
					{
						id: "segment",
						label: "Segment",
						value: segment === "baza" ? "Baza (AW)" : "Franczyza (AF)",
						active: segment !== "baza",
						picker: segmentSelect,
					},
				]}
				sheetContent={
					<>
						<MobileFilterField label="Hurtownia">{hurtowniaSelect}</MobileFilterField>
						<MobileFilterField label="Segment">{segmentSelect}</MobileFilterField>
					</>
				}
				onClear={() => setSegment("baza")}
				resultLabel={total > 0 ? `Pokaż ${total.toLocaleString("pl-PL")} pozycji` : "Pokaż cennik"}
			>
				{/* Toolbar kontrolek */}
				<div className="px-6 py-3 border-y bg-card/50 grid grid-cols-1 gap-4 md:grid-cols-3">
					<div className="flex flex-col gap-1.5">
						<Label>Hurtownia</Label>
						<Select
							value={idfdost != null ? String(idfdost) : undefined}
							onValueChange={(v) => setIdfdost(Number(v))}
						>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Wybierz hurtownię" />
							</SelectTrigger>
							<SelectContent>
								{wholesalers.map((w) => (
									<SelectItem key={w.idfdost} value={String(w.idfdost)}>
										{w.nazwa || `#${w.idfdost}`}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="flex flex-col gap-1.5">
						<Label>Segment</Label>
						<Select value={segment} onValueChange={(v) => setSegment(v as Segment)}>
							<SelectTrigger className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="baza">Baza (AW)</SelectItem>
								<SelectItem value="franczyza">
									{selectedHurt && !selectedHurt.maFranczyza
										? "Franczyza (= AW)"
										: "Franczyza (AF)"}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="flex flex-col gap-1.5">
						<Label htmlFor="cennik-szukaj">Szukaj</Label>
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
							<Input
								id="cennik-szukaj"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder={`Szukaj po nazwie lub ${etykietaKodu}…`}
								className="pl-10 bg-background"
								disabled={idfdost == null}
							/>
						</div>
					</div>
				</div>
			</MobileFilters>

			<div className="flex-1 min-h-0 overflow-auto p-6 pt-4 space-y-4 max-md:p-3 max-md:space-y-3">
				{idfdost == null ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Wybierz hurtownię.
						</CardContent>
					</Card>
				) : (
					<Tabs
						value={search.zakladka ?? "cennik"}
						onValueChange={(v) => patchSearch({ zakladka: v as "cennik" | "porownanie" })}
					>
						<TabsList>
							<TabsTrigger value="cennik">Cennik</TabsTrigger>
							{/* Porównanie jest rozszerzeniem tego, co użytkownik właśnie ogląda:
							    źródło A bierze się z wyboru u góry strony, więc drugi selektor
							    dokłada tylko stronę B. Osobna strona wymagałaby wybierania obu
							    od zera i byłaby piątym ekranem cennikowym w kartotece. */}
							<TabsTrigger value="porownanie">Porównanie</TabsTrigger>
						</TabsList>

						<TabsContent value="cennik" className="space-y-4 pt-4">
							{pusteOstatnie && (
								<div className="rounded-md border border-status-warning-border bg-status-warning-bg px-3 py-2 text-sm text-status-warning-text">
									<span className="font-medium">Ostatnie pobranie nie zawierało pozycji.</span>{" "}
									{fmtDateTime(pusteOstatnie.pobranoAt)}
									{pusteOstatnie.plikNazwa ? ` · plik ${pusteOstatnie.plikNazwa}` : ""} — plik
									dotarł, ale nie wniósł ani jednej pozycji, więc poniżej widzisz{" "}
									<span className="font-medium">poprzedni cennik</span>. Ceny są starsze niż data
									ostatniego pobrania.
								</div>
							)}
							{/* Naglowek cennika (metadane pobrania); na telefonie pod ikona info
							    w pasku. Brak cennika zostaje na miejscu jako stan pusty. */}
							{naglowek ? (
								<PageNote id="price-list-header" title="Pobrany cennik">
									<Card>
										<CardContent className="py-4 text-sm">
											<span className="text-muted-foreground">
												Cennik z dnia{" "}
												<span className="font-medium text-foreground">
													{fmtDateTime(naglowek.pobranoAt)}
												</span>
												, oferta z{" "}
												<span className="font-medium text-foreground">
													{fmtDate(naglowek.validFrom)}
												</span>{" "}
												· {naglowek.liczbaPoz ?? total} pozycji · {naglowek.pominieteBezBloz} bez
												kodu
												{naglowek.plikNazwa ? <> · plik {naglowek.plikNazwa}</> : null} ·{" "}
												{naglowek.zrodlo}
											</span>
										</CardContent>
									</Card>
								</PageNote>
							) : (
								<Card>
									<CardContent className="py-4 text-sm">
										<span className="text-muted-foreground">
											Brak pobranego cennika dla tej hurtowni/segmentu.
										</span>
									</CardContent>
								</Card>
							)}

							{/* Tabela pozycji */}
							<Card>
								<CardContent className="px-4 py-4 space-y-3">
									<div className="flex items-center justify-between gap-2 max-md:flex-wrap">
										<div className="text-xs text-muted-foreground tabular-nums">
											{total > 0 ? `Pozycje ${fromRow}–${toRow} z ${total}` : "Pozycje 0 z 0"}
										</div>
										<div className="flex items-center gap-2 max-md:flex-wrap max-md:[&>button]:h-9">
											<Button
												variant="outline"
												size="sm"
												onClick={() => {
													if (idfdost != null) zlecPobranie.mutate(idfdost);
												}}
												disabled={idfdost == null || isFetchingCennik || zlecPobranie.isPending}
												title="Zleca pobranie aktualnego cennika z FTP. Może potrwać od kilku do kilkudziesięciu sekund (daemon łapie zlecenie w ~20s)."
											>
												<RefreshCw
													className={`size-4${isFetchingCennik || zlecPobranie.isPending ? " animate-spin" : ""}`}
												/>
												{isFetchingCennik || zlecPobranie.isPending
													? "Sprawdzam…"
													: "Sprawdź i pobierz"}
											</Button>
											{nieodebrane && (
												<span className="text-[11px] text-status-warning-text">
													Zlecenie czeka w kolejce — żaden fetcher go nie podjął. Sprawdź, czy
													usługa <span className="font-mono">cenniki-fetcher</span> działa.
												</span>
											)}
											<Button
												variant="outline"
												size="sm"
												onClick={() => void eksportRef.current?.()}
												disabled={pozycje.length === 0}
											>
												<Download className="size-4" />
												Eksport do Excela
											</Button>
											<Button
												variant="outline"
												size="sm"
												onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
												disabled={!canPrev || isFetching}
											>
												Poprzednia
											</Button>
											<Button
												variant="outline"
												size="sm"
												onClick={() => setOffset((o) => o + PAGE_SIZE)}
												disabled={!canNext || isFetching}
											>
												Następna
											</Button>
										</div>
									</div>

									{fallbackAf && (
										<PageNote id="segment-af" title="Segment AF">
											<div className="mb-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
												Franczyza (AF) korzysta z cennika AW (baza).
											</div>
										</PageNote>
									)}

									{bledy.length > 0 && (
										<div className="mb-3 rounded-md border border-warning/30 bg-warning/10">
											<button
												type="button"
												onClick={() => setBledyOpen((o) => !o)}
												className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-warning"
											>
												{bledyOpen ? (
													<ChevronDown className="size-4 shrink-0" />
												) : (
													<ChevronRight className="size-4 shrink-0" />
												)}
												<span className="font-medium">⚠ Błędy cennika FM2: {bledy.length}</span>
												<span className="text-muted-foreground">
													ten sam {etykietaKodu} z różną ceną w pliku — wybrano najniższą
												</span>
											</button>
											{bledyOpen && (
												<div className="px-3 pb-3">
													<CennikBledyTable bledy={bledy} />
												</div>
											)}
										</div>
									)}

									{/* Ten sam DataTable co w zakładce Porównanie — spójny wygląd,
								    filtry w nagłówkach i wiersz podsumowania. Ograniczona wysokość
								    sprawia, że tabela scrolluje się wewnętrznie zamiast spychać
								    stopkę setki wierszy w dół. */}
									<div className="flex max-h-[70vh] min-h-[24rem] flex-col rounded-md border border-border bg-card">
										<DataTable
											tableId="cenniki.podglad"
											className="min-h-0 min-w-0 w-full flex-1"
											columns={kolumnyCennika}
											data={pozycje}
											isLoading={isLoading}
											enableToolbar
											enableColumnVisibility
											sorting={sorting}
											onSortingChange={setSorting}
											manualSorting={true}
											serverFiltering={true}
											columnFilters={columnFilters}
											onColumnFiltersChange={setColumnFilters}
											onExportReady={(fn) => {
												eksportRef.current = fn;
											}}
										/>
									</div>
								</CardContent>
							</Card>
						</TabsContent>

						<TabsContent value="porownanie" className="pt-4">
							<Card>
								<CardContent className="px-4 py-4">
									<CennikPorownaniePanel
										aIdfdost={idfdost}
										aSegment={segment}
										hurtownie={wholesalers}
										search={search}
										onPatchSearch={patchSearch}
									/>
								</CardContent>
							</Card>
						</TabsContent>
					</Tabs>
				)}
			</div>
		</div>
	);
}
